package dev.lingvo.site.components.editor

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import dev.lingvo.shared.KeyRow
import dev.lingvo.shared.SourceChangeDto
import dev.lingvo.shared.isCanonicalSourceReviewId
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.Session
import dev.lingvo.site.components.source.hasIdentity
import dev.lingvo.site.components.source.validCurrentSource
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.state.ProjectActionClaim
import dev.lingvo.site.state.ProjectPrivilege
import dev.lingvo.site.state.ProjectRouteContext
import dev.lingvo.site.state.liveProjectRoute
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch

/** An exact permalink selection is independent of the filtered queue's pages and counts. */
internal class EditorRequestedKeyStore(
    private val state: TranslationEditorState,
    private val scope: CoroutineScope,
    initialKeyId: String?,
) {
    var pendingId by mutableStateOf(initialKeyId)
        private set
    var failure by mutableStateOf<String?>(null)
        private set
    var loading by mutableStateOf(false)
        private set
    private var directId by mutableStateOf<String?>(null)
    private var generation = 0L

    val outsideLoadedQueue: Boolean
        get() = directId != null && directId == state.selectedKeyId && state.rows.none { it.key.id == directId }

    fun selected(keyId: String) {
        generation++
        pendingId = null
        failure = null
        loading = false
        if (directId != keyId) directId = null
    }

    fun preservesSelection(): Boolean = directId != null && directId == state.selectedKeyId

    fun retry() {
        if (loading || pendingId == null) return
        scope.launch { resolve(state.exactKeyRequestOwnership()) }
    }

    suspend fun resolve(requestIsCurrent: () -> Boolean) {
        val keyId = pendingId ?: return
        if (loading || !requestIsCurrent()) return
        val attempt = ++generation
        val session = Session.requestIdentity ?: return
        val bootstrap = state.bootstrap
        val route = ProjectRouteContext.current(state.projectSlug)
        val claim = route?.let { ProjectActionClaim.capture(it, Session.organizations, ProjectPrivilege.READ) }
        fun owned(): Boolean = attempt == generation && pendingId == keyId && requestIsCurrent() &&
            Session.requestIdentity == session && state.bootstrap == bootstrap &&
            (route == null || claim?.remainsAuthorized(liveProjectRoute(state.projectSlug), Session.organizations) == true)
        loading = true
        try {
            require(isCanonicalSourceReviewId(keyId)) {
                tr("editor.editor_requested_key_store.invalid_link", "The requested string link has an invalid identity.")
            }
            val project = checkNotNull(state.project)
            if (!owned()) return
            val detail = ApiClient.keyDetail(state.projectSlug, keyId)
            if (!owned()) return
            check(detail.key.id == keyId && detail.key.projectId == project.id && detail.sourceLocale == project.sourceLocale &&
                (state.locale == detail.sourceLocale || state.locale in detail.locales)) {
                tr("editor.editor_requested_key_store.identity_mismatch", "The requested string response did not match this project and link.")
            }
            val source = detail.translations.singleOrNull { it.locale == detail.sourceLocale && it.keyId == keyId }
                ?: error(tr("editor.editor_requested_key_store.source_missing", "The requested string has no readable canonical source. Inspect it before retrying."))
            var change: SourceChangeDto? = null
            if (state.sourceAwareQueueCaptured) {
                check(Session.serverFeatures.sourceAwareReview) {
                    tr("editor.editor_requested_key_store.source_support_unavailable", "Source-aware support is unavailable. Inspect the requested string or retry after support returns.")
                }
                if (!owned()) return
                change = ApiClient.sourceChange(state.projectSlug, keyId, state.locale)
                if (!owned()) return
                check(change.hasIdentity(keyId, state.locale)) {
                    tr("editor.editor_requested_key_store.comparison_identity_mismatch", "The source comparison did not match the requested string and language.")
                }
                val snapshot = change.validCurrentSource()
                check(change.currentSource == null || snapshot != null && snapshot.view.sourceLocale == detail.sourceLocale &&
                    snapshot.view.isPlural == detail.key.isPlural &&
                    snapshot.value == source.value && snapshot.pluralForms == source.pluralForms) {
                    tr("editor.editor_requested_key_store.source_changed_during_read", "The requested source changed while loading. Retry to read the complete string again.")
                }
            }
            val row = KeyRow(detail.key, source,
                target = if (change != null) change.target else detail.translations.singleOrNull { it.locale == state.locale && it.keyId == keyId },
                sourceSnapshot = change?.validCurrentSource())
            if (!owned()) return
            state.cacheExactRequestedRow(row)
            change?.let { state.sourceReview.changes[keyId] = Async.Success(it) }
            directId = keyId
            state.selectKey(keyId)
        } catch (cancelled: CancellationException) {
            throw cancelled
        } catch (problem: Throwable) {
            if (owned()) failure = problem.message?.takeIf(String::isNotBlank)
                ?: tr("editor.editor_requested_key_store.couldnt_load_requested_string", "Could not load the requested string. Your current work is retained.")
        } finally {
            if (attempt == generation) {
                loading = false
                // Another query may have finished while this read owned the single lookup slot.
                // Retire its evidence, but keep a deliberate retry instead of stranding the UUID.
                if (pendingId == keyId && failure == null && !owned()) failure = tr(
                    "editor.editor_requested_key_store.lookup_interrupted",
                    "The requested string lookup was interrupted. Your work is retained. Retry to read it in the current view.",
                )
            }
        }
    }
}
